import React from 'react';
import './GroupSlider.css';

interface GroupSliderProps {
  sliderValue: number;
  setSliderValue: (value: number) => void;
  sliderMode: 'groups' | 'playersPerGroup';
  setSliderMode: (mode: 'groups' | 'playersPerGroup') => void;
  maxGroups: number;
  totalPlayers: number;
  onRegenerateGroups: () => void;
}

const GroupSlider: React.FC<GroupSliderProps> = ({ 
  sliderValue, 
  setSliderValue, 
  sliderMode, 
  setSliderMode, 
  maxGroups, 
  totalPlayers,
  onRegenerateGroups
}) => {
  const getPlayersPerGroup = () => {
    if (sliderMode === 'groups') {
      return Math.ceil(totalPlayers / sliderValue);
    } else {
      return sliderValue;
    }
  };

  const getNumGroups = (playersPerGroup: number) => {
    return Math.ceil(totalPlayers / playersPerGroup);
  };

  const handleModeChange = (newMode: 'groups' | 'playersPerGroup') => {
    setSliderMode(newMode);
    if (newMode === 'playersPerGroup') {
      // Convert current groups to players per group
      const currentPlayersPerGroup = getPlayersPerGroup();
      setSliderValue(currentPlayersPerGroup);
    } else {
      // Convert current players per group to groups
      const currentPlayersPerGroup = sliderValue;
      const newNumGroups = getNumGroups(currentPlayersPerGroup);
      setSliderValue(Math.max(2, newNumGroups));
    }
  };

  const handleSliderChange = (value: number) => {
    setSliderValue(value);
  };

  const getSliderMax = () => {
    if (sliderMode === 'groups') {
      return maxGroups;
    } else {
      return totalPlayers;
    }
  };

  const getSliderMin = () => {
    if (sliderMode === 'groups') {
      return 2;
    } else {
      return 1;
    }
  };

  const getDescription = () => {
    if (sliderMode === 'groups') {
      return `Players will be randomly distributed into ${sliderValue} group${sliderValue !== 1 ? 's' : ''}`;
    } else {
      const playersPerGroup = sliderValue;
      const actualGroups = getNumGroups(playersPerGroup);
      return `Each group will have approximately ${playersPerGroup} player${playersPerGroup !== 1 ? 's' : ''} (${actualGroups} groups)`;
    }
  };

  return (
    <div className="slider-container">
      <div className="mode-toggle">
        <button
          className={`toggle-button ${sliderMode === 'groups' ? 'active' : ''}`}
          onClick={() => handleModeChange('groups')}
        >
          Number of Groups
        </button>
        <button
          className={`toggle-button ${sliderMode === 'playersPerGroup' ? 'active' : ''}`}
          onClick={() => handleModeChange('playersPerGroup')}
        >
          Players per Group
        </button>
      </div>

      <div className="slider-header">
        <h3 className="slider-title">
          {sliderMode === 'groups' ? 'Number of Groups' : 'Players per Group'}
        </h3>
        <span className="slider-value">{sliderValue}</span>
      </div>
      
      <div className="slider-wrapper">
        <input
          type="range"
          min={getSliderMin()}
          max={getSliderMax()}
          value={sliderValue}
          onChange={(e) => handleSliderChange(parseInt(e.target.value))}
          className="slider"
        />
        <div className="slider-labels">
          <span>{getSliderMin()}</span>
          <span>{getSliderMax()}</span>
        </div>
      </div>
      
      <p className="slider-description">
        {getDescription()}
      </p>
      
      <div className="regenerate-section">
        <button 
          onClick={onRegenerateGroups}
          className="regenerate-button"
        >
          🔄 Regenerate Groups
        </button>
        <p className="regenerate-note">
          Click to create new random groups with current settings
        </p>
      </div>
    </div>
  );
};

export default GroupSlider;
